import { Component, LaserSource, Detector } from '../engine/Component';
import { monochromaticToRGB, rgbToCSS } from '../utils/color';

export class ParameterPanel {
  private panel: HTMLElement;
  private component: Component | null = null;
  private onChange: () => void;
  private onBeforeChange?: () => void;
  private historyPushedForInteraction = false;
  private spectralCanvas: HTMLCanvasElement | null = null;
  private timeTraceCanvas: HTMLCanvasElement | null = null;
  private spectralUpdateId: number = 0;
  onPinDetector?: (detector: Detector) => void;
  isPinned?: (detectorId: number) => boolean;

  constructor(panelElement: HTMLElement, onChange: () => void, onBeforeChange?: () => void) {
    this.panel = panelElement;
    this.onChange = onChange;
    this.onBeforeChange = onBeforeChange;
  }

  show(component: Component): void {
    this.component = component;
    this.panel.classList.add('visible');
    this.render();
    if (component instanceof Detector) {
      this.startSpectralUpdates();
    } else {
      this.stopSpectralUpdates();
    }
  }

  hide(): void {
    this.component = null;
    this.panel.classList.remove('visible');
    this.stopSpectralUpdates();
  }

  private startSpectralUpdates(): void {
    this.stopSpectralUpdates();
    const update = () => {
      if (this.component instanceof Detector) {
        if (this.spectralCanvas) this.drawSpectralChart(this.component, this.spectralCanvas);
        if (this.timeTraceCanvas) this.drawTimeTrace(this.component, this.timeTraceCanvas);
      }
      this.spectralUpdateId = requestAnimationFrame(update);
    };
    this.spectralUpdateId = requestAnimationFrame(update);
  }

  private stopSpectralUpdates(): void {
    if (this.spectralUpdateId) {
      cancelAnimationFrame(this.spectralUpdateId);
      this.spectralUpdateId = 0;
    }
  }

  private render(): void {
    const comp = this.component;
    if (!comp) return;

    const typeName = comp.type.replace('_', ' ');
    let html = `<h3>${typeName.charAt(0).toUpperCase() + typeName.slice(1)}</h3>`;

    // Angle control (all components)
    const angleDeg = Math.round((comp.angle * 180 / Math.PI) % 360);
    html += this.sliderHtml('angle', 'Angle', angleDeg, 0, 360, 1, '\u00b0');

    // Component-driven parameter definitions
    const defs = comp.getParameterDefs();
    for (const def of defs) {
      if (def.type === 'checkbox') {
        const checked = def.toChecked ? def.toChecked(comp) : false;
        html += this.checkboxHtml(def.key, def.label, checked);
      } else {
        const displayValue = def.toDisplay ? def.toDisplay(comp) : 0;
        html += this.sliderHtml(def.key, def.label, displayValue, def.min ?? 0, def.max ?? 100, def.step ?? 1, def.unit ?? '');
      }

      // Color swatch for wavelength slider on lasers
      if (def.key === 'wavelength' && comp instanceof LaserSource) {
        const color = rgbToCSS(monochromaticToRGB(comp.wavelength));
        html += `<div class="color-swatch" style="margin-top:4px;height:4px;border-radius:2px;background:${color}"></div>`;
      }
    }

    // Charts for detectors
    if (comp instanceof Detector) {
      const pinLabel = this.isPinned?.(comp.id) ? 'Unpin Output' : 'Pin Output';
      html += `<button id="btn-pin-detector" style="width:100%;margin:6px 0;padding:4px 8px;font-size:11px;background:#2a2a40;color:#aab;border:1px solid #3a3a50;border-radius:4px;cursor:pointer;">${pinLabel}</button>`;
      html += `<h3 style="margin-top:6px;font-size:11px;">Intensity Profile</h3>`;
      html += `<canvas id="spectral-chart" width="200" height="80" style="width:100%;height:80px;border-radius:4px;background:rgba(0,0,0,0.3);"></canvas>`;
      html += `<h3 style="margin-top:8px;font-size:11px;">Intensity vs Time</h3>`;
      html += `<canvas id="time-trace" width="200" height="60" style="width:100%;height:60px;border-radius:4px;background:rgba(0,0,0,0.3);"></canvas>`;
    }

    this.panel.innerHTML = html;

    // Grab canvas references
    this.spectralCanvas = this.panel.querySelector('#spectral-chart') as HTMLCanvasElement | null;
    this.timeTraceCanvas = this.panel.querySelector('#time-trace') as HTMLCanvasElement | null;

    // Pin button for detectors
    const pinBtn = this.panel.querySelector('#btn-pin-detector');
    if (pinBtn && comp instanceof Detector) {
      const det = comp;
      pinBtn.addEventListener('click', () => {
        this.onPinDetector?.(det);
        this.render(); // re-render to update button label
      });
    }

    // Bind input events
    this.panel.querySelectorAll('input[type="range"]').forEach(input => {
      input.addEventListener('mousedown', () => {
        if (!this.historyPushedForInteraction) {
          this.historyPushedForInteraction = true;
          this.onBeforeChange?.();
        }
      });
      input.addEventListener('mouseup', () => {
        this.historyPushedForInteraction = false;
      });
      input.addEventListener('input', (e) => {
        const target = e.target as HTMLInputElement;
        this.handleSliderChange(target.dataset.param!, parseFloat(target.value));
      });
    });

    this.panel.querySelectorAll('input[type="checkbox"]').forEach(input => {
      input.addEventListener('change', (e) => {
        this.onBeforeChange?.();
        const target = e.target as HTMLInputElement;
        this.handleCheckboxChange(target.dataset.param!, target.checked);
      });
    });
  }

  private drawSpectralChart(detector: Detector, canvas: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const bins = detector.bins;
    const maxI = detector.getMaxIntensity();
    if (maxI < 1e-10) return;

    const barWidth = w / bins.length;

    for (let i = 0; i < bins.length; i++) {
      const bin = bins[i];
      const computedI = detector.getComputedIntensity(i);
      if (computedI < 1e-10) continue;

      const barHeight = (computedI / maxI) * (h - 4);
      const avgWavelength = bin.wavelengthWeighted > 0 ? bin.wavelengthWeighted / (bin.intensity || computedI) : 550e-9;
      const rgb = monochromaticToRGB(avgWavelength);

      ctx.fillStyle = rgbToCSS(rgb, 0.9);
      ctx.fillRect(i * barWidth, h - barHeight, barWidth, barHeight);
    }

    // Axis label
    ctx.font = '8px system-ui';
    ctx.fillStyle = 'rgba(200,200,200,0.5)';
    ctx.fillText('position', 2, h - 2);
  }

  private drawTimeTrace(detector: Detector, canvas: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const history = detector.intensityHistory;
    if (history.length < 2) return;

    const peak = detector.getHistoryPeak();
    if (peak < 1e-10) return;

    const padding = 2;
    const plotH = h - padding * 2;
    const plotW = w - padding * 2;

    // Draw grid lines
    ctx.strokeStyle = 'rgba(100,100,150,0.15)';
    ctx.lineWidth = 0.5;
    for (let i = 1; i <= 3; i++) {
      const y = padding + plotH * (1 - i / 4);
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(w - padding, y);
      ctx.stroke();
    }

    // Draw the trace
    ctx.beginPath();
    for (let i = 0; i < history.length; i++) {
      const x = padding + (i / (history.length - 1)) * plotW;
      const y = padding + plotH * (1 - history[i] / peak);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = '#44dd88';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Current value label
    const current = history[history.length - 1];
    ctx.font = '9px monospace';
    ctx.fillStyle = '#44dd88';
    ctx.textAlign = 'right';
    ctx.fillText(current.toFixed(3), w - 4, 10);
    ctx.textAlign = 'left';

    // Time label
    ctx.fillStyle = 'rgba(200,200,200,0.4)';
    ctx.font = '8px system-ui';
    ctx.fillText('time', 2, h - 2);
  }

  private sliderHtml(param: string, label: string, value: number, min: number, max: number, step: number, unit: string): string {
    return `
      <div class="param-group">
        <label>${label}</label>
        <input type="range" data-param="${param}" value="${value}" min="${min}" max="${max}" step="${step}" />
        <div class="param-value"><span id="val-${param}">${value}</span>${unit}</div>
      </div>`;
  }

  private checkboxHtml(param: string, label: string, checked: boolean): string {
    return `
      <div class="param-group" style="display:flex;align-items:center;gap:6px;">
        <input type="checkbox" data-param="${param}" ${checked ? 'checked' : ''} style="accent-color:#6060a0;" />
        <label style="margin:0;font-size:12px;color:#c0c0d0;">${label}</label>
      </div>`;
  }

  private handleSliderChange(param: string, value: number): void {
    const comp = this.component;
    if (!comp) return;

    const valSpan = this.panel.querySelector(`#val-${param}`);
    if (valSpan) valSpan.textContent = String(Math.round(value));

    if (param === 'angle') {
      comp.angle = value * Math.PI / 180;
    } else {
      const def = comp.getParameterDefs().find(d => d.key === param);
      if (def && def.fromDisplay) {
        comp.setParameter(param, def.fromDisplay(value));
      } else {
        comp.setParameter(param, value);
      }

      if (param === 'wavelength' && comp instanceof LaserSource) {
        const swatch = this.panel.querySelector('.color-swatch') as HTMLElement;
        if (swatch) swatch.style.background = rgbToCSS(monochromaticToRGB(comp.wavelength));
      }
    }

    this.onChange();
  }

  private handleCheckboxChange(param: string, checked: boolean): void {
    const comp = this.component;
    if (!comp) return;

    comp.setParameter(param, checked);
    this.render();
    this.onChange();
  }
}
